/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { disableAutoSelect } from '@/utils/googleAuth';
import { apiService, type AuthResponse } from '@/services/api';
import { extractString } from '@/utils/sqlNull';
import { authLogger } from '@/utils/logger';
import { getPrimaryRole } from '@/constants';
import type { User, AuthState, UserRole, RoleRequest } from '@/types';

interface AuthContextType extends AuthState {
  login: (userOrToken: User | string) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (role: UserRole) => void;
  loginAsGuest: () => void;
  submitRoleRequest: (request: Omit<RoleRequest, 'id' | 'submittedAt' | 'status'>) => Promise<void>;
  getRoleRequests: () => Promise<RoleRequest[]>;
  currentSemester: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SWITCH_ROLE'; payload: UserRole }
  | { type: 'SET_CURRENT_SEMESTER'; payload: string };

const authReducer = (state: AuthState & { currentSemester: string }, action: AuthAction): AuthState & { currentSemester: string } => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SWITCH_ROLE':
      if (state.user) {
        return {
          ...state,
          user: { ...state.user, role: action.payload },
        };
      }
      return state;
    case 'SET_CURRENT_SEMESTER':
      return {
        ...state,
        currentSemester: action.payload,
      };
    default:
      return state;
  }
};

// Global flag to prevent concurrent auth initialization across all instances
let authInitializationInProgress = false;
let authInitializationPromise: Promise<void> | null = null;

const initialState: AuthState & { currentSemester: string } = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  currentSemester: '',
};

// Convert backend user to frontend User type
const convertBackendUser = (backendUser: AuthResponse): User => {
  // Extract all available roles from the roles array
  const availableRoles = backendUser.roles.map(r => r.role as UserRole);
  
  // Determine primary role based on priority using constants
  const primaryRole = getPrimaryRole(availableRoles);
  
  // Extract name properly from SQL NullString using utility
  const userName = extractString(backendUser.user.name, backendUser.user.email.split('@')[0]);
  
  // Extract entity IDs from roles array
  // Maps backend entity_type to frontend User properties:
  // - 'mahasiswa' -> studentId (for students and alumni)
  // - 'dosen' -> employeeId (for lecturers)
  // - 'applicant' -> applicationId (for student applicants)
  // - Roles without entity_type have no associated entity (e.g., admin, guest)
  let studentId: string | undefined;
  let employeeId: string | undefined;
  let applicationId: string | undefined;
  
  if (backendUser.roles) {
    for (const roleEntity of backendUser.roles) {
      if (roleEntity.entity_type === 'mahasiswa' && roleEntity.entity_id) {
        studentId = roleEntity.entity_id;
      } else if (roleEntity.entity_type === 'dosen' && roleEntity.entity_id) {
        employeeId = roleEntity.entity_id;
      } else if (roleEntity.entity_type === 'applicant' && roleEntity.entity_id) {
        applicationId = roleEntity.entity_id;
      }
      // Roles without entity_type (like 'admin') are valid but have no entity association
    }
  }
  
  return {
    id: backendUser.user.id.toString(),
    email: backendUser.user.email,
    name: userName,
    role: primaryRole,
    avatar: undefined, // Can be added if backend supports it
    availableRoles, // All available roles from backend
    roleEntities: backendUser.roles, // Store raw role entities for reference
    studentId,
    employeeId,
    applicationId,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up auth error handler to auto-logout on token refresh failure
  useEffect(() => {
    apiService.setAuthErrorHandler(() => {
      authLogger.log('Auth error detected, logging out...');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    });
  }, []);

  useEffect(() => {
    // Check for stored auth state on app load
    const initAuth = async () => {
      // If initialization is already in progress, wait for it to complete
      if (authInitializationInProgress && authInitializationPromise) {
        authLogger.log('Auth initialization already in progress, waiting for completion');
        await authInitializationPromise;
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // Guard against concurrent calls
      if (authInitializationInProgress) {
        authLogger.log('Auth initialization already in progress, skipping duplicate call');
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      authInitializationInProgress = true;
      
      // Store the promise so other instances can wait for it
      authInitializationPromise = (async () => {
        try {
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            // Get the user's previously selected role from localStorage (if any)
            const storedUser = localStorage.getItem('user');
            let previouslySelectedRole: UserRole | null = null;
            
            if (storedUser) {
              try {
                const parsedStoredUser = JSON.parse(storedUser);
                previouslySelectedRole = parsedStoredUser.role || null;
              } catch (error) {
                authLogger.error('Error parsing stored user for role:', error);
              }
            }
            
            // Verify token is still valid by fetching user info
            const userWithRoles = await apiService.getCurrentUser();
            let user = convertBackendUser({
              access_token: accessToken,
              refresh_token: '',
              roles: userWithRoles.roles,
              user: userWithRoles.user,
            });
            
            // Restore the previously selected role if it's still available
            if (previouslySelectedRole && user.availableRoles && user.availableRoles.includes(previouslySelectedRole)) {
              user = { ...user, role: previouslySelectedRole };
            }
            
            dispatch({ type: 'LOGIN', payload: user });
            
            // Fetch current semester after successful login
            try {
              const semesterData = await apiService.getSystemSetting('current_semester');
              dispatch({ type: 'SET_CURRENT_SEMESTER', payload: semesterData.value });

            } catch (error) {
              authLogger.error('Failed to fetch current semester:', error);
            }
          } else {
            // Fallback to stored user (demo mode)
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              try {
                const user = JSON.parse(storedUser);
                dispatch({ type: 'LOGIN', payload: user });
              } catch (error) {
                authLogger.error('Error parsing stored user:', error);
                localStorage.removeItem('user');
              }
            }
          }
        } catch (error: unknown) {
          authLogger.error('Auth initialization failed:', error);

          const message = typeof error === 'object' && error !== null && 'message' in error ? String((error as { message?: unknown }).message) : '';
          // Only clear tokens if it's an authentication error (401/403)
          // Don't clear on network errors or other temporary issues
          const isAuthError = message.includes('401') || message.includes('403') || message.includes('Unauthorized');
          
          if (isAuthError) {
            apiService.clearTokens();
            localStorage.removeItem('user');
          }
        } finally {
          authInitializationInProgress = false;
          authInitializationPromise = null;
        }
      })();
      
      await authInitializationPromise;
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    initAuth();
  }, []);

  const login = useCallback(async (userOrToken: User | string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      if (typeof userOrToken === 'string') {
        // Backend authentication with Google token
        const authResponse = await apiService.loginWithGoogle(userOrToken);
        const user = convertBackendUser(authResponse);
        
        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'LOGIN', payload: user });
        
        // Fetch current semester after successful login
        try {
          const semesterData = await apiService.getSystemSetting('current_semester');
          dispatch({ type: 'SET_CURRENT_SEMESTER', payload: semesterData.value });
        } catch (error) {
          authLogger.error('Failed to fetch current semester:', error);
        }
      } else {
        // Demo/mock login (existing logic)
        localStorage.setItem('user', JSON.stringify(userOrToken));
        dispatch({ type: 'LOGIN', payload: userOrToken });
      }
    } catch (error) {
      authLogger.error('Login failed:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      authLogger.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
      // Prevent immediate re-login if Google auto select is active
      try {
        disableAutoSelect();
      } catch (err) {
        authLogger.warn('disableAutoSelect failed', err);
      }
    }
  }, []);

  const switchRole = useCallback((role: UserRole) => {
    if (state.user) {
      const updatedUser = { ...state.user, role };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      dispatch({ type: 'SWITCH_ROLE', payload: role });
    }
  }, [state.user]);

  const loginAsGuest = useCallback(() => {
    const guestUser: User = {
      id: `guest-${Date.now()}`,
      email: 'guest@system.local',
      name: 'Guest User',
      role: 'guest',
    };
    login(guestUser);
  }, [login]);

  const submitRoleRequest = useCallback(async (requestData: Omit<RoleRequest, 'id' | 'submittedAt' | 'status'>) => {
    try {
      await apiService.submitRoleRequest({
        requested_role: requestData.requestedRole,
        reason: requestData.reason,
      });
    } catch (error) {
      authLogger.error('Failed to submit role request:', error);
      // Fallback to localStorage for demo mode
      const request: RoleRequest = {
        ...requestData,
        id: `REQ-${Date.now()}`,
        submittedAt: new Date().toISOString(),
        status: 'pending',
      };
      const existingRequests = JSON.parse(localStorage.getItem('roleRequests') || '[]');
      const updatedRequests = [...existingRequests, request];
      localStorage.setItem('roleRequests', JSON.stringify(updatedRequests));
    }
  }, []);

  const getRoleRequests = useCallback(async (): Promise<RoleRequest[]> => {
    try {
      const backendRequests = await apiService.getRoleRequests();
      
      // Convert backend format to frontend format
      return backendRequests.map(req => ({
        id: req.id,
        userId: req.user_id,
        requestedRole: req.requested_role as UserRole,
        currentRole: 'guest' as UserRole, // Backend doesn't return this
        status: req.status,
        reason: req.reason,
        submittedAt: new Date(req.created_at).toLocaleString(),
      }));
    } catch (error) {
      console.error('Failed to get role requests:', error);
      // Fallback to localStorage for demo mode
      const requests = localStorage.getItem('roleRequests');
      return requests ? JSON.parse(requests) : [];
    }
  }, []);

  const value = {
    ...state,
    login,
    logout,
    switchRole,
    loginAsGuest,
    submitRoleRequest,
    getRoleRequests,
    currentSemester: state.currentSemester,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};